import { FC } from 'react';
import { ReactComponent as SkipIcon } from 'resources/images/skip-solid.svg';
import { ReactComponent as NextIcon } from 'resources/images/next-solid.svg';
import { Box, Icon, Text, Flex, Grid } from '@chakra-ui/react';
import { useAppSelector } from 'app/store';
import { isExtendedPlayer } from 'app/store/AudioPlayer/selectors';
import { AudioActionButton } from '../../Song/Audio/AudioActionButton';
import { RepeatButton } from '../RepeatButton';
import { getFormattedTime } from '../utils';
import { AudioPlayerTrack } from '../AudioPlayerTrack';
import { audioPlayerControlsSx } from './styles';
import { PlaylistButton } from '../PlaylistButton';
import { LyricsButton } from '../LyricsButton';

export interface AudioPlayerControlsComponentProps {
  handleRewind: () => void;
  handleSkip?: () => void;
  duration: number;
  isLoading: boolean;
  isPlaying: boolean;
  onPlayPause: () => void;
  isPlaylistMode: boolean;
  currentTime: number;
  src?: string;
  isError?: boolean;
  toggleLoop: () => void;
  loop: boolean;
  isPlayerExpandable: boolean;
}

export const AudioPlayerControlsComponent: FC<AudioPlayerControlsComponentProps> = ({
  handleRewind,
  handleSkip,
  duration,
  isLoading,
  isPlaying,
  onPlayPause,
  isPlaylistMode,
  currentTime,
  src,
  isError,
  toggleLoop,
  loop,
  isPlayerExpandable,
}) => {
  const extendedPlayer = useAppSelector(isExtendedPlayer);

  const durationText =
    duration && !isLoading && src
      ? getFormattedTime(duration, duration)
      : '--:--';

  const styles = audioPlayerControlsSx({ extendedPlayer, isPlaylistMode });

  return (
    <Grid sx={styles.playerControls}>
      <Box gridArea="controls">
        <Grid sx={styles.controls}>
          <Icon
            sx={{
              gridArea: 'rewind',
              ...styles.rewindButton,
            }}
            data-testid="audio-rewind-button"
            onClick={(e) => {
              e.stopPropagation();
              handleRewind();
            }}
            as={SkipIcon}
          />
          <AudioActionButton
            playing={isPlaying}
            onClick={(e) => {
              e.stopPropagation();
              onPlayPause();
            }}
            data-testid="audio-play-pause-button"
            isLoading={isLoading}
            size={extendedPlayer ? 64 : 36}
            isPlayerTheme
            playIconTestId="audio-play-icon"
            pauseIconTestId="audio-pause-icon"
            thickness={2}
          />
          {isPlaylistMode && (
            <Icon
              sx={styles.skipButton}
              data-testid="audio-skip-button"
              onClick={(e) => {
                e.stopPropagation();
                handleSkip?.();
              }}
              as={NextIcon}
            />
          )}
          {extendedPlayer && (
            <>
              <RepeatButton isActive={loop} onClick={toggleLoop} />
              <LyricsButton />
              {isPlayerExpandable && isPlaylistMode && <PlaylistButton />}
            </>
          )}
        </Grid>
      </Box>
      <Text sx={styles.timeMark}>
        {currentTime ? getFormattedTime(currentTime, duration) : '0:00'}
      </Text>
      <AudioPlayerTrack
        duration={duration}
        isLoadingAudio={isLoading}
        src={src}
        isError={isError}
      />
      {!extendedPlayer && <Text sx={styles.separator}>/</Text>}
      <Text sx={styles.durationText}>{durationText}</Text>
      <Flex gap={5}>
        {!extendedPlayer && (
          <>
            <RepeatButton isActive={loop} onClick={toggleLoop} />
            <LyricsButton />
          </>
        )}
        {!extendedPlayer && isPlayerExpandable && isPlaylistMode && (
          <PlaylistButton />
        )}
      </Flex>
    </Grid>
  );
};
